import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Input from "components/Input/Input";
import Button from "components/Button/Regular";
import Select from "components/Input/Select";
import { useAuth0 } from "@auth0/auth0-react";
import { readSelfProfile, updateUserProfile } from "services/profile.service";
import vgToast from "utils/toast";

type FormValues = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  systemLanguage: string;
};

// Only the languages the backend's ENUM_SYS_LanguageKey supports.
export const LANG_TO_KEY: Record<string, string> = {
  english: "en_gb",
  swedish: "sv",
  portuguese: "pt",
};

export const KEY_TO_LANG = (k: string | undefined): string => {
  if (!k) return "english";
  const lower = k.toLowerCase();
  if (lower.includes("sv")) return "swedish";
  if (lower.includes("pt")) return "portuguese";
  return "english";
};

const UpdateProfileForm = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const userIdRef = React.useRef<number>(0);

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      emailAddress: "",
      systemLanguage: "english",
    },
  });

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadError(null);
      try {
        const token = await getAccessTokenSilently();
        const { data, error } = await readSelfProfile(token);
        if (cancelled) return;
        if (error) {
          setLoadError(error.message);
          return;
        }
        if (!data) return;
        userIdRef.current = data.userId;
        // Read_Self_Profile can return empty contact fields when the signed-in
        // user has no UserMember-type contact row yet. Fall back to whatever
        // Auth0 knows so the form isn't blank on first visit.
        const auth0First =
          (user?.given_name as string | undefined) ||
          (user?.name as string | undefined)?.split(" ")[0] ||
          "";
        const auth0Last =
          (user?.family_name as string | undefined) ||
          (user?.name as string | undefined)?.split(" ").slice(1).join(" ") ||
          "";
        reset({
          firstName: data.contactFirstName || auth0First,
          lastName: data.contactLastName || auth0Last,
          emailAddress: data.emailAddress || user?.email || "",
          systemLanguage: KEY_TO_LANG(data.languageKey),
        });
      } catch (e) {
        if (!cancelled) setLoadError((e as Error).message);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, reset, user]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (userIdRef.current <= 0) {
      vgToast.error("Profile not loaded yet.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const languageKey = LANG_TO_KEY[data.systemLanguage] ?? "en_gb";
      const { error } = await updateUserProfile(token, {
        userId: userIdRef.current,
        emailAddress: data.emailAddress,
        contactFirstName: data.firstName,
        contactLastName: data.lastName,
        languageKey,
      });
      if (error) vgToast.error(error.message);
      else vgToast.success("Profile saved.");
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-10">
      {loadError && (
        <div className="mb-4 text-sm text-red-500">{loadError}</div>
      )}
      <div className="grid grid-cols-1 gap-6 mb-10 sm:grid-cols-2 sm:gap-10">
        <Input
          {...register("firstName")}
          label="First Name"
          placeholder="Enter First Name"
        />

        <Input
          {...register("lastName")}
          label="Last Name"
          placeholder="Enter Last Name"
        />

        <Input
          {...register("emailAddress")}
          label="Email Address"
          placeholder="Enter Your Email"
        />

        <Select
          {...register("systemLanguage")}
          label="System Language"
          placeholder="System Language"
          options={[
            { title: "English", value: "english" },
            { title: "Swedish", value: "swedish" },
            { title: "Portuguese", value: "portuguese" },
          ]}
        />
      </div>

      <Button color="Primary" type="submit" disabled={submitting}>
        <span>{submitting ? "Saving…" : "Save profile"}</span>
      </Button>
    </form>
  );
};

export default UpdateProfileForm;
